import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import {
  BillingFormData,
  NewClientData,
} from '@/containers/billing/register/billingPortals/newRegisterWizard/NewRegisterWizard'
import { ClientInterface } from '@/interfaces/ServerInterfaces'
import { cn } from '@/lib/utils'

import { ClientSelector, NewClientForm } from './components'
import { ClientData, ClientValidationErrors } from './types'

// Simplified validation schema
const clientManagerSchema = z.object({
  mode: z.enum(['select', 'create']),
  selectedClientId: z.string().optional(),
  clientNotes: z.string().optional(),
})

// Conditional validation based on mode and required prop
const createValidationSchema = (required: boolean) => {
  if (!required) return clientManagerSchema

  return clientManagerSchema.refine(
    (data) => {
      if (data.mode === 'select') {
        return !!data.selectedClientId
      }
      return true // For create mode, we validate newClientData separately
    },
    {
      message: 'Debe seleccionar un cliente',
      path: ['selectedClientId'],
    }
  )
}

type FormData = z.infer<typeof clientManagerSchema>

// Data structure that matches BillingFormData expectations
interface ClientManagerResult {
  clientId?: string
  isNewClient: boolean
  newClientData?: NewClientData
  selectedClientData?: ClientInterface
  clientNotes?: string
}

// Exposed methods through ref
export interface ClientManagerRef {
  submit: () => Promise<void>
}

interface ClientManagerProps {
  // Event callbacks (SOLID: Dependency Inversion)
  onSubmit?: (data: ClientManagerResult) => void | Promise<void>
  onValidationChange?: (isValid: boolean) => void
  onDataChange?: (data: ClientManagerResult) => void // New callback for data sync

  // Configuration
  required?: boolean
  disabled?: boolean
  className?: string
  clientContainer?: boolean

  // Initial values
  data: Partial<BillingFormData>
}

export const ClientManager = forwardRef<ClientManagerRef, ClientManagerProps>(
  (
    {
      required = true,
      disabled = false,
      clientContainer = false,
      className,
      data,
      onSubmit,
      onValidationChange,
      onDataChange,
    },
    ref
  ) => {
    // Internal state for new client data and selected client data
    const [newClientData, setNewClientData] = useState<
      NewClientData | undefined
    >(data?.newClientData)
    const [selectedClientData, setSelectedClientData] = useState<
      ClientInterface | undefined
    >(data?.selectedClientData)
    const [validationErrors, setValidationErrors] = useState<
      Record<string, string>
    >({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

    // Main form with react-hook-form
    const form = useForm<FormData>({
      resolver: zodResolver(createValidationSchema(required)),
      mode: 'onChange',
      defaultValues: {
        mode: data?.isNewClient ? 'create' : 'select',
        selectedClientId: data?.clientId,
      },
    })

    const { control, watch, setValue, handleSubmit } = form
    const currentMode = watch('mode')
    const watchedClientNotes = watch('clientNotes')
    const watchedSelectedClientId = watch('selectedClientId')

    // Validate all client data (both existing and new clients)
    const validateClientData = useCallback((): {
      isValid: boolean
      errors: Record<string, string>
    } => {
      const errors: Record<string, string> = {}

      if (currentMode === 'create') {
        // For new client creation, need complete newClientData
        if (!newClientData) {
          errors.general = 'Datos del nuevo cliente no válidos'
          return { isValid: false, errors }
        }

        if (!newClientData.name?.trim()) {
          errors.name = 'El nombre es obligatorio'
        } else if (newClientData.name.length < 2) {
          errors.name = 'El nombre debe tener al menos 2 caracteres'
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(newClientData.name)) {
          errors.name = 'El nombre solo puede contener letras y espacios'
        }

        if (!newClientData.lastName?.trim()) {
          errors.lastName = 'Los apellidos son obligatorios'
        } else if (newClientData.lastName.length < 2) {
          errors.lastName = 'Los apellidos deben tener al menos 2 caracteres'
        } else if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/.test(newClientData.lastName)) {
          errors.lastName =
            'Los apellidos solo pueden contener letras y espacios'
        }

        // if (!newClientData.email?.trim()) {
        //   errors.email = 'El correo electrónico es obligatorio'
        // } else

        if (
          newClientData.email &&
          !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
            newClientData.email
          )
        ) {
          errors.email = 'El correo electrónico no tiene un formato válido'
        }

        if (!newClientData.phone?.number?.trim()) {
          errors.phone = 'El teléfono es obligatorio'
        } else if (!/^\d{8,10}$/.test(newClientData.phone.number)) {
          errors.phone =
            'El teléfono debe contener solo números y tener entre 8 y 10 dígitos'
        }
        if (!newClientData.phone?.codeCountry?.trim()) {
          errors.phoneCode = 'El código de país es obligatorio'
        }

        if (!newClientData.mainAddress?.trim())
          errors.mainAddress = 'La calle principal es obligatoria'
        if (!newClientData.country?.name?.trim())
          errors.country = 'El país es obligatorio'
        if (!newClientData.province?.id?.trim())
          errors.province = 'La provincia es obligatoria'
        if (!newClientData.municipality?.id?.trim())
          errors.municipality = 'El municipio es obligatorio'
      } else {
        // For existing client selection, need clientId and selectedClientData
        if (required && (!watchedSelectedClientId || !selectedClientData)) {
          errors.general = 'Debe seleccionar un cliente para continuar'
        }
      }

      const isValidResult = Object.keys(errors).length === 0
      return { isValid: isValidResult, errors }
    }, [
      currentMode,
      newClientData,
      watchedSelectedClientId,
      selectedClientData,
      required,
    ])

    // Helper to notify parent of validation status
    const notifyValidationChange = useCallback(
      (isValid: boolean) => {
        console.log('ClientManager - Notifying validation change:', isValid)
        onValidationChange?.(isValid)
      },
      [onValidationChange]
    )

    // Helper to notify parent of state changes (for data sync only)
    const notifyDataChange = useCallback(() => {
      const currentData: ClientManagerResult = {
        clientId: watchedSelectedClientId,
        isNewClient: currentMode === 'create',
        selectedClientData:
          currentMode === 'select' ? selectedClientData : undefined,
        newClientData: currentMode === 'create' ? newClientData : undefined,
        clientNotes: watchedClientNotes || '',
      }

      console.log(
        'ClientManager - Notifying data change (sync only):',
        currentData
      )
      onDataChange?.(currentData)
    }, [
      currentMode,
      watchedSelectedClientId,
      watchedClientNotes,
      selectedClientData,
      newClientData,
      onDataChange,
    ])

    // Handle form submission (for internal submit buttons and external triggers)
    const onFormSubmit = async (formData: FormData) => {
      console.log('ClientManager - Form submit triggered')
      setHasAttemptedSubmit(true)

      // Validate all client data
      const validation = validateClientData()
      console.log('ClientManager - Submit validation result:', validation)

      if (!validation.isValid) {
        console.log('ClientManager - Validation failed, showing errors')
        setValidationErrors(validation.errors)
        notifyValidationChange(false)
        return
      }

      setIsSubmitting(true)
      try {
        const submitData: ClientManagerResult = {
          clientId: formData.selectedClientId,
          isNewClient: currentMode === 'create',
          selectedClientData:
            currentMode === 'select' ? selectedClientData : undefined,
          newClientData: currentMode === 'create' ? newClientData : undefined,
          clientNotes: formData.clientNotes,
        }

        // Clear errors on successful validation
        setValidationErrors({})
        notifyValidationChange(true)
        console.log(
          'ClientManager - Submitting valid data (SUCCESS):',
          submitData
        )
        await onSubmit?.(submitData)
      } catch (error) {
        console.error('Error submitting client data:', error)
      } finally {
        setIsSubmitting(false)
      }
    }

    // Expose submit function through ref
    useImperativeHandle(
      ref,
      () => ({
        submit: async () => {
          const formData = {
            mode: currentMode,
            selectedClientId: watchedSelectedClientId,
            clientNotes: watchedClientNotes,
          }
          await onFormSubmit(formData)
        },
      }),
      [currentMode, watchedSelectedClientId, watchedClientNotes, onFormSubmit]
    )

    // Only run validation when user has attempted to submit or we have existing errors
    useEffect(() => {
      if (hasAttemptedSubmit) {
        const validation = validateClientData()
        console.log(
          'ClientManager - Re-validating after submit attempt:',
          validation
        )
        setValidationErrors(validation.errors)
        notifyValidationChange(validation.isValid)
      }
    }, [
      currentMode,
      watchedSelectedClientId,
      newClientData,
      selectedClientData,
      hasAttemptedSubmit,
      validateClientData,
      notifyValidationChange,
    ])

    useEffect(() => {
      if (clientContainer) handleNewClientClick()
    }, [])

    // Notify parent of data changes (for synchronization only, not submission)
    useEffect(() => {
      notifyDataChange()
    }, [notifyDataChange])

    // Initial validation state - assume valid until user tries to submit
    useEffect(() => {
      if (!hasAttemptedSubmit) {
        // Start with valid state to allow user interaction
        notifyValidationChange(true)
      }
    }, [notifyValidationChange, hasAttemptedSubmit])

    // Handle client selection
    const handleClientSelect = (client: ClientData) => {
      console.log('ClientManager - Client selected:', client)
      setValue('mode', 'select')
      setValue('selectedClientId', client.id.toString())
      // Convert ClientData to ClientInterface format
      const clientInterface: ClientInterface = {
        id: client.id,
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        name:
          client.name ||
          `${client.firstName || ''} ${client.lastName || ''}`.trim(),
        email: client.email || '',
        ci: client.ci ? parseInt(client.ci) : undefined,
        observations: null, // Always null for ClientInterface
        commercial: client.commercial
          ? {
              id: client.commercial.id,
              username: client.commercial.name, // Map name to username
              email: '', // Required field, use empty string as default
              isActive: true,
              isSuperAdmin: false,
              isLogued: false,
              lastLogin: null,
              displayName: client.commercial.name,
              businessId: 0, // Default value
              avatarId: 0, // Default value
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              deletedAt: null,
              avatar: {
                id: 0,
                src: '',
                thumbnail: '',
                blurHash: '',
              },
              roles: [],
              allowedStockAreas: [],
              allowedSalesAreas: [],
              allowedManufacturerAreas: [],
              allowedAccessPointAreas: [],
              allowedBusiness: [],
            }
          : null,
        // Add other required ClientInterface fields with defaults
        contractNumber: '',
        organization: '',
        codeClient: 0,
        businessId: 0,
        address: {
          id: 0,
          street: '',
          description: '',
          locality: '',
          city: '',
          municipality: {
            id: 0,
            name: '',
            code: '',
            province: {
              id: 0,
              name: '',
              code: '',
            },
          },
          province: {
            id: 0,
            name: '',
            code: '',
          },
          country: {
            id: 54,
            name: 'Cuba',
            code: 'CU',
          },
          countryId: null,
          shippingRegionId: 2, // Use proper type for shippingRegionId
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          postalCode: null,
          coordinateLatitude: null,
          coordinateLongitude: null,
          municipalityId: null,
          provinceId: null,
          deletedAt: null,
        },
        phones: [],
        addressId: 0,
        legalNotes: '',
        barCode: '',
        registrationWay: '',
        customerCategory: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        deletedAt: '',
      }
      setSelectedClientData(clientInterface)
      setNewClientData(undefined)

      // Clear errors and reset submit state when user makes a selection
      setValidationErrors({})
      setHasAttemptedSubmit(false)

      // Since user made a valid selection, notify parent that data is now valid
      notifyValidationChange(true)
    }

    // Handle switch to new client mode
    const handleNewClientClick = (initialName: string = '') => {
      console.log('ClientManager - Switching to new client mode')
      setValue('mode', 'create')
      setValue('selectedClientId', undefined)
      // DON'T clear selectedClientData - preserve it for when user switches back
      // setSelectedClientData(undefined)
      setNewClientData({
        name: initialName,
        lastName: '',
        email: '',
        phone: { number: '', codeCountry: '+53' },
        categories: [],
        mainAddress: '',
        country: {
          id: '54',
          name: 'Cuba',
        },
        province: { id: '', name: '' },
        municipality: { id: '', name: '' },
      })
      setValidationErrors({})
      setHasAttemptedSubmit(false)

      // When switching to new client mode, data is not valid yet but allow user to fill form
      notifyValidationChange(true) // Allow user to interact with form
    }

    // Handle switch back to search
    const handleSwitchToSearch = () => {
      console.log('ClientManager - Switching back to search')
      setValue('mode', 'select')

      // If we have preserved selectedClientData, restore it
      if (selectedClientData) {
        setValue('selectedClientId', selectedClientData.id.toString())
        console.log(
          'ClientManager - Restored previous client selection:',
          selectedClientData.name
        )
      } else {
        setValue('selectedClientId', undefined)
      }

      // DON'T clear selectedClientData - it's already preserved
      // setSelectedClientData(undefined)
      setNewClientData(undefined)
      setValidationErrors({})
      setHasAttemptedSubmit(false)

      // When switching back to search, validate based on whether we have a selection
      notifyValidationChange(!!selectedClientData || !required)
    }

    // Handle new client data changes
    const handleNewClientDataChange = (updates: Partial<NewClientData>) => {
      if (newClientData) {
        const updatedData = { ...newClientData, ...updates }
        console.log('ClientManager - New client data updated:', updatedData)
        setNewClientData(updatedData)

        // Clear errors for changed fields only if user hasn't attempted submit
        if (!hasAttemptedSubmit) {
          const clearedErrors = { ...validationErrors }
          Object.keys(updates).forEach((key) => {
            delete clearedErrors[key]
          })
          setValidationErrors(clearedErrors)
        }
      }
    }

    // Handle field change to clear internal errors only
    const handleFieldChange = (field: keyof ClientValidationErrors) => {
      if (!hasAttemptedSubmit) {
        const clearedErrors = { ...validationErrors }
        delete clearedErrors[field]
        setValidationErrors(clearedErrors)
      }
    }

    // Handle focus on fields to clear validation errors
    const handleFieldFocus = (field: string) => {
      // Clear internal error only if user hasn't attempted submit
      if (!hasAttemptedSubmit) {
        const clearedErrors = { ...validationErrors }
        delete clearedErrors[field]
        setValidationErrors(clearedErrors)
      }
    }

    // Convert ClientInterface back to ClientData for display
    const displaySelectedClient = selectedClientData
      ? ({
          id: selectedClientData.id,
          name: selectedClientData.name,
          firstName: selectedClientData.firstName,
          lastName: selectedClientData.lastName,
          email: selectedClientData.email,
          ci: selectedClientData.ci?.toString(),
          observations: selectedClientData.observations || undefined, // Convert null to undefined
          commercial: selectedClientData.commercial
            ? {
                id: selectedClientData.commercial.id,
                name: selectedClientData.commercial.displayName,
              }
            : undefined,
        } as unknown as ClientData) // Use unknown cast for type safety
      : undefined

    return (
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className={cn('space-y-6', className)}
      >
        {/* Client Selection or New Client Form */}

        {currentMode === 'select' ? (
          <Controller
            control={control}
            name="selectedClientId"
            render={({ field, fieldState }) => (
              <ClientSelector
                selectedClient={displaySelectedClient}
                onClientSelect={handleClientSelect}
                onNewClientClick={handleNewClientClick}
                required={required}
                disabled={disabled || isSubmitting}
                error={fieldState.error?.message || validationErrors.general}
                onFocus={() => handleFieldFocus('general')}
              />
            )}
          />
        ) : (
          <>
            <Separator />
            {newClientData && (
              <NewClientForm
                clientData={newClientData}
                onClientDataChange={handleNewClientDataChange}
                onSwitchToSearch={handleSwitchToSearch}
                validationErrors={validationErrors}
                onFieldChange={handleFieldChange}
                onFieldFocus={handleFieldFocus}
                disabled={disabled || isSubmitting}
                clientContainer={clientContainer}
              />
            )}
          </>
        )}
      </form>
    )
  }
)
